import axios from 'axios';
import IApiResponse from '@/types/IApiResponse';
import { route } from 'ziggy-js';
import { ref } from 'vue';
import {
    client as BraintreeClient,
    hostedFields as BraintreeHostedFields,
    threeDSecure as BraintreeThreeDSecure,
    Client,
    HostedFields,
    ThreeDSecure,
    HostedFieldFieldOptions,
    ThreeDSecureVerifyPayload,
} from 'braintree-web';
import { ThreeDSecureVerifyOptions } from 'braintree-web/three-d-secure';
import ITeamCardAdditionalInformation from '@/types/Members/ITeamCardAdditionalInformation';
import { IPaymentMethod } from '@/types/Subscription/IPaymentMethod';

const hostedFieldsStyles = ref({
    input: {
        'font-size': '14px',
        color: '#4B5563',
    },
    ':focus': {
        color: '#252F3F',
    },
    '.invalid': {
        color: '#E02424',
    },
});

const hostedFields = ref<HostedFieldFieldOptions>({
    number: {
        selector: '#cardNumber',
        placeholder: 'Enter card number',
    },
    cardholderName: {
        selector: '#cardholderName',
        placeholder: 'Enter name on card',
    },
    cvv: {
        selector: '#cardCvv',
        placeholder: 'XXX',
    },
    expirationDate: {
        selector: '#cardExpirationDate',
        placeholder: 'MM/YY',
    },
});

export const useBraintreeClient = (billingAddressType: 'pro' | 'plugin') => {
    const paymentMethodNonce = ref('');
    const threeDSecureInstance = ref<ThreeDSecure | undefined>(undefined);
    const threeDSecureParameters = ref<ThreeDSecureVerifyOptions | undefined>(
        undefined
    );
    const gatewayInstance = ref<Client | undefined>(undefined);
    const paymentGatewayToken = ref<string | undefined>(undefined);
    const hostedFieldsInstance = ref<HostedFields | undefined>(undefined);
    const bin = ref<string | undefined>(undefined);

    const createClient = async ({
        enableHostedFields,
    }: {
        enableHostedFields: boolean;
    }) => {
        await teardownClient();

        await getPaymentGatewayToken();

        try {
            gatewayInstance.value = await BraintreeClient.create({
                authorization: paymentGatewayToken.value,
            });

            if (enableHostedFields) {
                await createHostedFields();
            }
        } catch (err) {
            return;
        }
    };

    const teardownClient = async () => {
        if (gatewayInstance.value) {
            await gatewayInstance.value.teardown(() => {});
        }

        if (hostedFieldsInstance.value) {
            await hostedFieldsInstance.value.teardown(() => {});
        }

        if (threeDSecureInstance.value) {
            await threeDSecureInstance.value.teardown(() => {});
        }
    };

    const getPaymentGatewayToken = async () => {
        try {
            const response = await axios.post<IApiResponse<string>>(
                route('braintree.payment-gateway-token'),
                {
                    billing_address_type: billingAddressType,
                }
            );

            paymentGatewayToken.value = response.data.data;
        } catch (err) {
            return;
        }
    };

    const createHostedFields = async () => {
        try {
            hostedFieldsInstance.value = await BraintreeHostedFields.create({
                client: gatewayInstance.value,
                styles: hostedFieldsStyles.value,
                fields: hostedFields.value,
            });
        } catch (err) {
            return;
        }
    };

    const tokenize = async () => {
        try {
            if (hostedFieldsInstance.value === undefined) {
                return;
            }

            const res = await hostedFieldsInstance.value.tokenize();
            paymentMethodNonce.value = res.nonce;
            bin.value = res?.details.bin;
        } catch (err) {
            return;
        }
    };

    const validateCardResponse = (
        verifyCardResponse: ThreeDSecureVerifyPayload | undefined
    ) => {
        if (verifyCardResponse === undefined) {
            throw new Error('Card verification failed');
        }

        if (
            verifyCardResponse.threeDSecureInfo.liabilityShifted === false &&
            verifyCardResponse.threeDSecureInfo.liabilityShiftPossible === true
        ) {
            throw new Error('Card verification failed');
        }
    };

    const verifyCard = async ({
        card_additional_information,
        nonce,
        amount,
        token,
    }: {
        card_additional_information: null | ITeamCardAdditionalInformation;
        nonce?: string;
        amount?: number;
        token?: string;
    }): Promise<ThreeDSecureVerifyPayload> => {
        try {
            if (!nonce) {
                await tokenize();
            }

            await getThreeDSecureParameters({
                card_additional_information: card_additional_information,
                nonce: nonce,
                amount: amount,
                token: token,
                bin: bin.value,
            });

            await initThreeDSecureClient();

            if (
                threeDSecureInstance.value === undefined ||
                threeDSecureParameters.value === undefined
            ) {
                throw new Error('Undefined 3d secure instance');
            }

            threeDSecureParameters.value.challengeRequested = true;

            const verifyCardResponse =
                await threeDSecureInstance.value.verifyCard(
                    threeDSecureParameters.value
                );

            validateCardResponse(verifyCardResponse);

            return verifyCardResponse;
        } catch (err) {
            throw new Error('Card verification failed');
        }
    };

    const getThreeDSecureParameters = async ({
        card_additional_information,
        nonce,
        amount,
        token,
        bin,
    }: {
        card_additional_information: ITeamCardAdditionalInformation | null;
        nonce?: string;
        amount?: number;
        token?: string;
        bin?: string;
    }) => {
        const response = await axios.post<
            IApiResponse<ThreeDSecureVerifyOptions>
        >(route('braintree.three-d-secure-parameters'), {
            billing_address_type: billingAddressType,
            card_additional_information: card_additional_information,
            amount: amount,
            card_token: token,
        });

        threeDSecureParameters.value = response.data.data;
        threeDSecureParameters.value.amount =
            threeDSecureParameters.value.amount / 100;
        threeDSecureParameters.value.nonce = nonce ?? paymentMethodNonce.value;
        threeDSecureParameters.value.bin = bin;

        //@ts-ignore
        threeDSecureParameters.value.onLookupComplete = (data, next) => {
            next();
        };
    };

    const initThreeDSecureClient = async () => {
        try {
            threeDSecureInstance.value = await BraintreeThreeDSecure.create({
                version: 2,
                client: gatewayInstance.value,
            });
        } catch (err) {
            throw new Error('ThreeDSecure init error!');
        }
    };

    const verifyCardByToken = async ({
        token,
        amount,
    }: {
        token: string;
        amount: number;
    }) => {
        const nonce = await getNonceForToken(token);

        return verifyCard({
            card_additional_information: null,
            nonce: nonce,
            amount: amount,
            token: token,
        });
    };

    const getNonceForToken = async (token: string) => {
        const response = await axios.post(
            route('braintree.payment-method.nonce.store'),
            {
                token: token,
            }
        );

        bin.value = response.data.data.bin;

        return response.data.data.nonce;
    };

    const addPaymentMethod = async ({
        nonce,
        card_additional_information,
    }: {
        nonce: string;
        card_additional_information: ITeamCardAdditionalInformation | null;
    }) => {
        try {
            const response = await axios.post(
                route('braintree.payment-methods.create'),
                {
                    nonce: nonce,
                    billing_address_type: billingAddressType,
                    card_additional_information: card_additional_information,
                }
            );

            create({
                title: response.data.success
                    ? 'Payment method added'
                    : 'Adding payment method failed',
                text: response.data.success
                    ? 'Your new payment method has been successfully added.'
                    : response.data.errors.paymentMethod,
                type: response.data.success ? 'success' : 'error',
            });
        } catch (err) {
            create({
                title: 'Payment method not added',
                text: 'There has been an error adding your payment method.',
                type: 'error',
            });
        }
    };

    const getPaymentMethods = async () => {
        return axios.get<IApiResponse<IPaymentMethod[]>>(
            route('braintree.payment-methods.index', {
                billing_address_type: billingAddressType as string,
            })
        );
    };

    const setDefaultPaymentMethod = async (token: string) => {
        return axios.post<IApiResponse<unknown>>(
            route('braintree.payment-methods.make-default'),
            {
                token: token,
                type: billingAddressType,
            }
        );
    };

    const deletePaymentMethod = async (token: string) => {
        return axios.post<IApiResponse<unknown>>(
            route('braintree.payment-methods.delete'),
            {
                token: token,
                type: billingAddressType,
            }
        );
    };

    return {
        createClient,
        teardownClient,
        getPaymentGatewayToken,
        tokenize,
        validateCardResponse,
        verifyCard,
        paymentGatewayToken,
        verifyCardByToken,
        addPaymentMethod,
        getPaymentMethods,
        setDefaultPaymentMethod,
        deletePaymentMethod,
    };
};
